import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { errMessage, ORG_LOGOS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in successfully");
      const target = loc.state?.from?.pathname || "/dashboard";
      nav(target, { replace: true });
    } catch (err) {
      setError(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] grid lg:grid-cols-2" data-testid="login-page">
      <div className="relative hidden lg:block">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-[#0F2A47] to-primary" />
        <div className="absolute inset-0 bg-grid-navy opacity-30" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <img src={ORG_LOGOS.ACIS} alt="ACIS" className="h-10 w-10 object-contain" />
              <img src={ORG_LOGOS.IBTHM} alt="IBTHM" className="h-10 w-10 object-contain" />
            </div>
            <div className="font-heading font-bold text-base">ACIS | IBTHM</div>
          </div>
          <div className="max-w-md">
            <ShieldCheck className="h-8 w-8 text-[#C5A059] mb-4" />
            <h2 className="font-heading text-3xl font-bold tracking-tight leading-tight">
              The portal for credentialed professionals and applicants.
            </h2>
            <p className="mt-4 text-white/80 text-sm leading-relaxed">
              Track your application, manage your credentials, upload supporting documents, and submit
              renewals — all from one secure portal.
            </p>
          </div>
          <div className="text-xs text-white/50">All access events are logged for compliance.</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10 bg-slate-50">
        <Card className="w-full max-w-md p-8 border-slate-200 shadow-sm bg-white">
          <div className="mb-6">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">Sign in</div>
            <h1 className="font-heading text-2xl font-bold text-primary">Welcome back.</h1>
            <p className="text-sm text-slate-500 mt-1">Use your credentialing portal account.</p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label>Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                data-testid="login-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
            <Button data-testid="login-submit" disabled={loading} className="w-full bg-primary text-white hover:bg-[#113A64] h-11">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-center text-sm text-slate-600">
              No account?{" "}
              <Link to="/register" className="text-primary font-medium hover:underline" data-testid="register-link">
                Apply for credentials
              </Link>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
